import { FormControl, FormGroup } from '@angular/forms';
import {
  CustomCodeFormShape,
  CustomCodeVariableDoc,
  createCustomCodeFormGroup,
} from '../custom-code/custom-code.model';

export type TitleMode = 'text' | 'logo';
export type IconSize = 'small' | 'medium' | 'large';
/** Same scale the Goals Progress Bar offers. Drives the bar's vertical padding. */
export type TopBarPadding = 'none' | 'small' | 'medium' | 'large';

export interface TopBarFormShape {
  backgroundColor: FormControl<string>;
  padding: FormControl<TopBarPadding>;
  bottomBorder: FormGroup<{
    enabled: FormControl<boolean>;
    color: FormControl<string>;
  }>;
  title: FormGroup<{
    mode: FormControl<TitleMode>;
    text: FormControl<string>;
    logoUrl: FormControl<string>;
    textColor: FormControl<string>;
  }>;
  closeButton: FormGroup<{
    iconColor: FormControl<string>;
    backgroundColor: FormControl<string>;
    hoverColor: FormControl<string>;
    iconSize: FormControl<IconSize>;
  }>;
  customCode: FormGroup<CustomCodeFormShape>;
}

const TOP_BAR_DEFAULTS = {
  backgroundColor: '#FFFFFF',
  // 'medium' resolves to the 16px the top bar has always rendered, so adding this control leaves
  // existing carts looking exactly as they did.
  padding: 'medium' as TopBarPadding,
  bottomBorder: { enabled: true, color: '#EEEEEE' },
  title: {
    mode: 'text' as TitleMode,
    text: '<strong>My Cart ({item_count})</strong>',
    logoUrl: '',
    // Empty means inherit the storefront theme's colour, which is what the title did before this
    // control existed — so existing carts keep the exact look they have today.
    textColor: '',
  },
  closeButton: {
    iconColor: '#666666',
    backgroundColor: '#FFFFFF',
    hoverColor: '#FFFFFF',
    iconSize: 'small' as IconSize,
  },
};

export const TOP_BAR_VARIABLES: CustomCodeVariableDoc[] = [
  {
    reference: '{{componentId}}',
    type: 'String',
    description: 'Unique widget ID, useful as a DOM hook for your own CSS or JS',
  },
  {
    reference: '{{backgroundColor}}',
    type: 'String',
    description: "Hex value from the admin's background color picker",
  },
  {
    reference: '{{bottomBorder.enabled}}',
    type: 'Boolean',
    description: 'True when the bottom border toggle is on in the admin',
  },
  {
    reference: '{{bottomBorder.color}}',
    type: 'String',
    description: "Hex value from the admin's border color picker",
  },
  {
    reference: '{{title.text}}',
    type: 'String',
    description:
      'Configured title text with cart count already substituted — render with triple braces to preserve formatting',
  },
  {
    reference: '{{title.textColor}}',
    type: 'String',
    description: "Hex of the title's text colour in Text mode; empty means inherit from the theme",
  },
  { reference: '{{closeButton.iconColor}}', type: 'String', description: "Hex of the close button's icon" },
  { reference: '{{closeButton.backgroundColor}}', type: 'String', description: "Hex of the close button's background" },
  { reference: '{{closeButton.hoverColor}}', type: 'String', description: "Hex of the close button's hover state" },
  { reference: '{{itemCount}}', type: 'Number', description: 'Current number of items in the cart' },
];

export const TOP_BAR_STARTER_TEMPLATE = `<div
  style="display: flex; justify-content: space-between; align-items: center; padding: 16px 20px; background-color: {{backgroundColor}};{{#if bottomBorder.enabled}} border-bottom: 1px solid {{bottomBorder.color}};{{/if}}"
>
  <div class="title">{{{title.text}}}</div>
  <button
    data-pdq-action="close"
    aria-label="Close cart"
    style="background-color: {{closeButton.backgroundColor}}; color: {{closeButton.iconColor}}; border: none; cursor: pointer; padding: 8px; border-radius: 4px; font-size: 20px;"
  >&times;</button>
</div>`;

export function createTopBarFormGroup(initialSettings?: Record<string, unknown> | null): FormGroup<TopBarFormShape> {
  const formGroup = new FormGroup<TopBarFormShape>({
    backgroundColor: new FormControl(TOP_BAR_DEFAULTS.backgroundColor, { nonNullable: true }),
    padding: new FormControl(TOP_BAR_DEFAULTS.padding, { nonNullable: true }),
    bottomBorder: new FormGroup({
      enabled: new FormControl(TOP_BAR_DEFAULTS.bottomBorder.enabled, { nonNullable: true }),
      color: new FormControl(TOP_BAR_DEFAULTS.bottomBorder.color, { nonNullable: true }),
    }),
    title: new FormGroup({
      mode: new FormControl<TitleMode>(TOP_BAR_DEFAULTS.title.mode, { nonNullable: true }),
      text: new FormControl(TOP_BAR_DEFAULTS.title.text, { nonNullable: true }),
      logoUrl: new FormControl(TOP_BAR_DEFAULTS.title.logoUrl, { nonNullable: true }),
      textColor: new FormControl(TOP_BAR_DEFAULTS.title.textColor, { nonNullable: true }),
    }),
    closeButton: new FormGroup({
      iconColor: new FormControl(TOP_BAR_DEFAULTS.closeButton.iconColor, { nonNullable: true }),
      backgroundColor: new FormControl(TOP_BAR_DEFAULTS.closeButton.backgroundColor, { nonNullable: true }),
      hoverColor: new FormControl(TOP_BAR_DEFAULTS.closeButton.hoverColor, { nonNullable: true }),
      iconSize: new FormControl<IconSize>(TOP_BAR_DEFAULTS.closeButton.iconSize, { nonNullable: true }),
    }),
    customCode: createCustomCodeFormGroup(undefined, TOP_BAR_STARTER_TEMPLATE),
  });

  if (initialSettings) {
    formGroup.patchValue(initialSettings);
  }

  return formGroup;
}
